import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  Button,
  Input,
  LoadingIcon,
  Modal,
  Tooltip,
} from 'vet-bones/design-system/components';

import { useAppDispatch, useAppSelector } from 'src/ui/app/hooks';
import {
  RENAME_MY_SHARED_ENDPOINT_DIALOG_CANCEL,
  RENAME_MY_SHARED_ENDPOINT_DIALOG_NAME,
  RENAME_MY_SHARED_ENDPOINT_DIALOG_SUBMIT,
} from 'src/ui/constants/testIds';
import { useInvalidateConnection } from 'src/ui/containers/endpoints/utils/invalidate';
import { clearConnection } from 'src/ui/features/connection';
import { DialogType, closedDialog } from 'src/ui/features/ui';
import { graphQLClient } from 'src/ui/graph/graphQLClient';
import { useRenameMySharedConnectionMutation } from 'src/ui/graph/types';
import { useCurrentConnection } from 'src/ui/hooks/connection';
import { useOnError, useOnMutate } from 'src/ui/hooks/graph';
import { editIndexSelector } from 'src/ui/selectors/connection';
import { isDialogOpenSelector } from 'src/ui/selectors/isDialogOpen';
import * as copy from 'src/ui/templates/copy';

const isOpenSelector = isDialogOpenSelector(
  DialogType.RENAME_MY_SHARED_ENDPOINT
);

export const RenameMySharedEndpointDialog: React.VFC = () => {
  const { org = '' } = useParams<{ org?: string }>();
  const dispatch = useAppDispatch();
  const onError = useOnError();
  const onMutate = useOnMutate();

  const isOpen = useAppSelector(isOpenSelector);
  const editIndex = useAppSelector(editIndexSelector);

  const { connection } = useCurrentConnection(editIndex.toString(), org);
  const invalidateConnection = useInvalidateConnection(
    '',
    connection?.id,
    connection?.index
  );

  const {
    isLoading: isRenaming,
    mutate: renameConnection,
  } = useRenameMySharedConnectionMutation(graphQLClient, {
    onMutate,
    onError,
    onSuccess: () => {
      invalidateConnection();
      dispatch(closedDialog());
      dispatch(clearConnection());
    },
  });

  const [name, setName] = React.useState('');

  React.useEffect(() => {
    if (connection) {
      setName(connection.name);
    }
  }, [connection]);

  const handleClose = () => {
    dispatch(closedDialog());
    setName('');
  };

  const handleSubmit = () => {
    const trimmedName = name.trim();

    if (!connection || !trimmedName) {
      return;
    }

    renameConnection({
      input: {
        connectionId: connection.id || '',
        name: trimmedName,
      },
    });
  };

  let submitTooltip = '';
  if (!name.trim()) {
    submitTooltip = copy.components.endpoints.inputs.name.validationRequired;
  }

  return (
    <Modal
      canOutsideClickClose={false}
      className="endpoint-dialog"
      footerActions={
        <>
          <Button
            onClick={handleClose}
            size="sm"
            testId={RENAME_MY_SHARED_ENDPOINT_DIALOG_CANCEL}
            variant="ghost-gray"
          >
            {
              copy.components.endpoints.renameMySharedEndpointDialog.buttons
                .cancel
            }
          </Button>
          <Tooltip description={submitTooltip} disabled={!submitTooltip}>
            <Button
              disabled={isRenaming || Boolean(submitTooltip)}
              leftIcon={isRenaming ? LoadingIcon : undefined}
              onClick={handleSubmit}
              size="sm"
              testId={RENAME_MY_SHARED_ENDPOINT_DIALOG_SUBMIT}
              variant="primary-accent"
            >
              {
                copy.components.endpoints.renameMySharedEndpointDialog.buttons
                  .submit
              }
            </Button>
          </Tooltip>
        </>
      }
      headerSize="lg"
      isOpen={Boolean(isOpen && connection)}
      onClose={handleClose}
      title={copy.components.endpoints.renameMySharedEndpointDialog.title}
    >
      <div className="endpoint-dialog-body">
        <form
          className="endpoint-dialog-form"
          onSubmit={(evt) => {
            evt.preventDefault();
            handleSubmit();
          }}
        >
          <Input
            autoFocus
            clearable={false}
            id="sd-connection-name"
            label={copy.components.endpoints.inputs.name.label}
            onChange={(e) => setName(e.target.value)}
            required
            size="md"
            testId={RENAME_MY_SHARED_ENDPOINT_DIALOG_NAME}
            value={name}
          />

          <input hidden type="submit" />
        </form>
      </div>
    </Modal>
  );
};
